import React from "react";

const STATUS_CONFIG = {
  covered: {
    border: "border-l-[brand-green]",
    borderColor: "#2E7D32",
    bg: "",
  },
  "missing-head": {
    border: "border-l-[brand-red]",
    borderColor: "#E53935",
    bg: "bg-[#FFEBEA]",
  },
  "double-head": {
    border: "border-l-[brand-warn]",
    borderColor: "#F5A623",
    bg: "",
  },
  unassigned: {
    border: "border-l-[brand-orange]",
    borderColor: "#FF5A1F",
    bg: "bg-[#FFE8DD]",
  },
};

const FLAG_STYLES = {
  covered:        "bg-[#E8F5E9] text-[#2E7D32]",
  "missing-head": "bg-[#FFEBEA] text-[#E53935]",
  "double-head":  "bg-[#FFF4E0] text-[#F5A623]",
  unassigned:     "bg-[#FFE8DD] text-[#FF5A1F]",
};

function StaffBadge({ role }) {
  if (role === "admin") {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0"
        style={{ background: "#6B21A8", border: "2.5px solid #D8B4FE", color: "#D8B4FE", fontWeight: 700, fontSize: 7 }}>
        AD
      </span>
    );
  }
  if (role === "head") {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0"
        style={{ background: "#F55C00", border: "2.5px solid #111", color: "white", fontWeight: 700, fontSize: 9 }}>
        L
      </span>
    );
  }
  if (role === "manager") {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0"
        style={{ background: "#111", border: "2.5px solid #F55C00", color: "#F55C00", fontWeight: 700, fontSize: 9 }}>
        M
      </span>
    );
  }
  // assistant
  return (
    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0"
      style={{ background: "white", border: "2.5px solid #111", color: "#111", fontWeight: 700, fontSize: 9 }}>
      A
    </span>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.75 3.5h10.5M5.25 3.5V2.333a.583.583 0 0 1 .583-.583h2.334a.583.583 0 0 1 .583.583V3.5M11.083 3.5l-.583 7.583a.583.583 0 0 1-.583.584H4.083a.583.583 0 0 1-.583-.584L2.917 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5.833 6.417v2.916M8.167 6.417v2.916" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

export default function EventCard({
  time,
  title,
  evType,
  evSpot,
  staff = [],
  status = "covered",
  flagLabel,
  statusPill,
  notes,
  scheduledBy,
  onClick,
  onDelete,
}) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.covered;
  const flagCls = FLAG_STYLES[status] || FLAG_STYLES.covered;

  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && onClick(e) : undefined}
      className={[
        "relative font-sans rounded-xl border border-[#EFEFEF] border-l-4",
        "py-[14px] px-[16px]",
        "transition-all duration-150",
        onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md" : "",
        cfg.bg,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ borderLeftColor: cfg.borderColor }}
    >
      {/* Top row: time pill + optional flag (left) + optional statusPill (right) */}
      <div className="flex items-center justify-between gap-2 mb-[7px]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-[11px] font-bold bg-gray-100 text-[#0A0A0A] rounded px-[7px] py-[2px] leading-none tracking-tight flex-shrink-0">
            {time}
          </span>
          {flagLabel && (
            <span className={`text-[10px] font-semibold rounded-full px-[8px] py-[2px] leading-none uppercase tracking-wide ${flagCls}`}>
              {flagLabel}
            </span>
          )}
        </div>
        {statusPill}
      </div>
      {/* Title */}
      <div className="font-sans font-bold text-[15px] leading-tight tracking-tight text-[#0A0A0A] mb-[5px]" style={{ textTransform: "uppercase" }}>
        {title}
      </div>

      {/* Type + spot row */}
      {(evType || evSpot) && (
        <div className="mb-[8px]" style={{ fontSize: 11, lineHeight: 1.4 }}>
          {evType && (
            <span style={{ color: "#F55C00", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
              {evType}
            </span>
          )}
          {evType && evSpot && (
            <span style={{ color: "#6B6B6B" }}> · </span>
          )}
          {evSpot && (
            <span style={{ color: "#111", fontSize: 11 }}>
              {evSpot}
            </span>
          )}
        </div>
      )}

      {notes && (
        <p style={{ fontSize: 12, fontStyle: "italic", color: "#0A0A0A", margin: "3px 0 0", lineHeight: 1.4 }}>{notes}</p>
      )}

      {/* Staff chips */}
      {staff.length > 0 && (
        <div className="flex flex-wrap gap-[6px] mt-[2px]">
          {staff.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-[5px] bg-white border border-[#EFEFEF] rounded-full px-[8px] py-[3px] text-[11px] text-[#0A0A0A] font-medium"
            >
              <StaffBadge role={s.role} />
              {s.name}
            </span>
          ))}
        </div>
      )}

      {/* Scheduled by */}
      {scheduledBy && (
        <div style={{ position: "absolute", bottom: 10, right: onDelete ? 34 : 12, fontSize: 10, color: "#111", fontStyle: "italic" }}>
          by {scheduledBy}
        </div>
      )}

      {/* Delete button */}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute bottom-[10px] right-[12px] text-gray-300 hover:text-[#E53935] transition-colors"
          aria-label="Delete event"
        >
          <TrashIcon />
        </button>
      )}
    </div>
  );
}
